import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { borderGrey, darkerBG, mediumText, secondaryText, smallTextSize } from '../../styling/stylingHelper';
import { FrontendStaffUser, addRestaurantToStaff } from '../../services/staff/staffService';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../ui/InventorySlideOver';
import FilterDropdown from '../pages/reports/hub/utilization/FilterDropdown';

interface Props {
  isOpen: boolean;
  staff: FrontendStaffUser;
  onClose: () => void;
  onAdded: () => void;
}

const ManageStaffRestaurantsModal: React.FC<Props> = ({ isOpen, staff, onClose, onAdded }) => {
  const { t } = useTranslation();
  const all = useRestaurantStore((s) => s.restaurants);

  const [choice, setChoice] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () => all.filter((r) => !staff.restaurant_ids.includes(r.id)),
    [all, staff.restaurant_ids]
  );

  const dropdownOptions = useMemo(() => options.map((r) => ({ value: r.id, label: r.name })), [options]);

  useEffect(() => {
    if (isOpen) {
      setChoice(options[0]?.id || '');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, staff]);

  const handleAdd = async () => {
    if (!choice) {
      setError(t('staff.errorSelectRestaurant'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await addRestaurantToStaff(staff.id, choice);
      onAdded();
    } catch (err) {
      console.error(err);
      setError(t('staff.errorAddRestaurant'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('staff.addRestaurant')}
      subtitle={staff.email}
      closeLabel={t('common.close')}
      footer={
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass} disabled={saving}>
            {t('staff.cancel')}
          </button>
          <button
            type="button"
            onClick={handleAdd}
            className={slideOverPrimaryClass}
            disabled={saving || !choice || options.length === 0}
          >
            {saving ? t('staff.adding') : t('staff.add')}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className={cn('p-4 rounded-lg border mb-6', borderGrey, darkerBG)}>
        <p className={cn(secondaryText, mediumText, 'mb-3 text-[16px] leading-7')}>{t('staff.selectRestaurant')}</p>
        {options.length === 0 ? (
          <p className={cn(secondaryText, smallTextSize)}>{t('staff.noRestaurants')}</p>
        ) : (
          <FilterDropdown
            label={t('staff.selectPlaceholder')}
            options={dropdownOptions}
            value={choice}
            onChange={setChoice}
            searchable
            fullWidth
          />
        )}
      </div>
    </InventorySlideOver>
  );
};

export default ManageStaffRestaurantsModal;
